'use client';

import { useGoogleLogin, useGoogleOAuth } from '@react-oauth/google';
import { useTranslation } from 'next-i18next';
import { useCallback } from 'react';

import GoogleIcon from '@/assets/svg/google-icon';
import { SSO_BTN_GOOGLE } from '@/constants/sso-button-classes';
import { useAuth } from '@/hooks/use-auth';
import { apiErrorMessageKey } from '@/utils/api-error-i18n';
import { trackSsoSignUp } from '@/utils/track-sso-sign-up';
import { trackSsoTrialStart } from '@/utils/track-sso-trial-start';

import type { SsoButtonProps } from './types';

// `autoStart` is deliberately NOT read here: Google's implicit flow opens a popup, and a popup with no
// user gesture behind it is blocked by default in Chrome/Edge/Firefox. Starting it on mount could never
// sign anyone in — it landed in `onNonOAuthError` ('popup_failed_to_open') and painted an error on a page
// the user had just opened to sign in. The click IS the gesture, so this button always waits for it.
// Only DiscordSsoButton honours `autoStart`, and only because a full-page redirect needs no gesture.
export default function GoogleSsoButton({ onAuthStart, onAuthSuccess, onAuthError, desktopRedirectUri, trialContext }: SsoButtonProps) {
  const auth = useAuth();
  const { t } = useTranslation('common');
  // Until Google's gsi/client script has loaded, `useGoogleLogin` has not built its token client and the
  // `login()` it returns is `clientRef.current?.requestAccessToken(...)` — optional-chained, so calling it
  // does nothing at all and reports nothing. That used to be unreachable: GoogleOAuthProvider lived at the
  // app root, so the script was long loaded by the time any button mounted. Now the provider mounts WITH
  // this button (to keep 97 KB of gsi/client off pages with no sign-in), and React runs child effects
  // before parent effects — so at first mount the script has not even been appended yet.
  const { scriptLoadedSuccessfully } = useGoogleOAuth();

  const login = useGoogleLogin({
    flow: 'implicit',
    scope: 'openid email profile',
    onSuccess: ({ access_token }) => {
      auth.loginWithGoogle(
        { token: access_token, ...trialContext },
        (error) => {
          if (onAuthError) onAuthError(t(apiErrorMessageKey(error, { fallbackKey: 'login.google_error_generic' })));
        },
        (loginToken, result) => {
          // Desktop handoff (RFC 8252): hand the single-use login-token to the desktop client's
          // loopback listener via a real navigation instead of the normal in-app dashboard flow.
          if (desktopRedirectUri && loginToken) {
            window.location.href = `${desktopRedirectUri}?token=${encodeURIComponent(loginToken)}`;
            return;
          }
          trackSsoSignUp(result);
          trackSsoTrialStart(result, trialContext);
          if (onAuthSuccess) onAuthSuccess(result);
        }
      );
    },
    onError: () => {
      if (onAuthError) onAuthError(t('login.google_error_generic'));
    },
    onNonOAuthError: (err) => {
      // popup_closed: user cancelou — libera o form silenciosamente.
      if (err.type === 'popup_closed') {
        if (onAuthSuccess) onAuthSuccess();
        return;
      }
      // popup_failed_to_open: o browser barrou o popup, o login nem foi tentado — "não foi possível
      // entrar" manda o usuário tentar de novo pelo mesmo caminho que acabou de ser bloqueado.
      if (err.type === 'popup_failed_to_open') {
        if (onAuthError) onAuthError(t('login.google_error_popup_blocked'));
        return;
      }
      if (onAuthError) onAuthError(t('login.google_error_generic'));
    },
  });

  const handleClick = useCallback(() => {
    // Guard before `onAuthStart`, not after: the callers use it to raise AuthLoadingOverlay, which covers
    // the panel (and the close button). Firing it for a login() that no-ops leaves the user on a spinner
    // that nothing will ever clear, because no success or error callback is coming.
    if (!scriptLoadedSuccessfully) return;
    if (onAuthStart) onAuthStart();
    login();
  }, [scriptLoadedSuccessfully, login, onAuthStart]);

  return (
    <button type='button' onClick={handleClick} disabled={!scriptLoadedSuccessfully} className={SSO_BTN_GOOGLE}>
      <GoogleIcon className='size-5' />
      {t('login.continue_with_google')}
    </button>
  );
}
